/**
 * In-game saves panel — browse/load/save/delete saves for the CURRENT game
 * without leaving the session. Solo-only: loading a state mid-netplay would
 * desync lockstep, so the toolbar button is hidden outside State.SOLO
 * (see _showScreen in main.js).
 *
 * Load applies the state in-place via app._applySaveInPlace(); C64
 * multi-disk, BASIC-mode, and stale-hash saves fall back to the session
 * reboot loaders (app._loadSaveFromDB / app._loadSaveFromServer).
 *
 * Data: the SaveSync server index is authoritative (solo play is
 * auth-gated). Local IndexedDB rows are merged in, deduped by kind/slot,
 * so a just-created save shows before its background upload lands.
 * Manual-save server slots default to the per-device IndexedDB id; when
 * that number was taken, the upload landed elsewhere and the local row
 * records it as `serverSlot` — pairing keys on serverSlot ?? id so a row
 * merges with ITS server copy, not another device's same-id save. Merged
 * rows always LOAD from the server copy; delete removes both sides so the
 * merge can't resurrect a deleted row. (Legacy rows that predate the
 * create-only server guard may still share a slot across devices.)
 */
import { currentSaveDBNamespace, listSaves, getSave, deleteSave } from '../saves.js';
import { decodeEmulatorState } from '../../shared/save-state-codec.js';
import { primaryHash } from '../../shared/rom-hash.js';
import { moduleRegistry } from '../module-registry.js';
import { noteSurfaceOpened } from './surface-order.js';

/** Per-game manual save-state cap mirrored client-side (see saves.js). */
const MANUAL_STATE_CAP = 10;

let _app = null;
let _activeTab = 'manual';
let _deleteArmedKey = null;
let _deleteTimer = 0;

/**
 * Merge server metas + local saves into display rows, deduped by kind/slot.
 * Pure — exported for tests. Both inputs are already filtered to the
 * current game by the caller.
 */
export function buildSaveRows(serverSaves, localSaves) {
  const rows = new Map();
  for (const meta of serverSaves) {
    if (meta.kind === 'disk') continue;
    const key = `${meta.kind}/${meta.slot}`;
    rows.set(key, {
      key,
      name: meta.label || (meta.kind === 'sram' ? 'Auto Save' : 'Save'),
      date: meta.updated_at || null,
      isAuto: meta.kind === 'sram',
      deviceLabel: meta.device_label || null,
      localId: null,
      server: { system: meta.system_id, romHash: meta.rom_hash, kind: meta.kind, slot: meta.slot },
      isMultiDisk: false,
      isBasicMode: false,
    });
  }
  for (const s of localSaves) {
    // Reallocated saves live under serverSlot on the server; legacy rows
    // under their IDB id.
    const key = s.isAutoSave ? 'sram/auto' : `state/${String(s.serverSlot ?? s.id)}`;
    const existing = rows.get(key);
    if (existing) {
      // Server copy is authoritative for load; remember the local row
      // occupying this slot so delete can remove both sides. (Cross-device
      // it may be a different save sharing the slot number — see header.)
      // ??= keeps the NEWEST local row when stale duplicates share a key
      // (listSaves returns newest-first).
      existing.localId ??= s.id;
      continue;
    }
    rows.set(key, {
      key,
      name: s.name || (s.isAutoSave ? 'Auto Save' : 'Save'),
      date: s.createdAt || null,
      isAuto: !!s.isAutoSave,
      deviceLabel: null,
      localId: s.id,
      server: null,
      isMultiDisk: !!s.isMultiDisk,
      isBasicMode: !!s.isBasicMode,
    });
  }
  return [...rows.values()].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export function isSavesPanelOpen() {
  const el = document.getElementById('saves-overlay');
  return !!el && !el.classList.contains('hidden');
}

/** Bind the toolbar button + overlay controls. Call once from _bindUI. */
export function initSavesPanel(app) {
  _app = app;
  document.getElementById('btn-saves-panel')
    ?.addEventListener('click', () => { openSavesPanel(app).catch(() => {}); });
  document.getElementById('saves-overlay-close')
    ?.addEventListener('click', () => closeSavesPanel());
  document.getElementById('saves-overlay-save-now')
    ?.addEventListener('click', () => { saveNow(app).catch(() => {}); });
  // Backdrop click closes; clicks inside the content card have a different
  // target and fall through.
  document.getElementById('saves-overlay')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeSavesPanel();
  });
}

export async function openSavesPanel(app) {
  // 'solo' === State.SOLO — string literal avoids a circular import of main.js.
  if (app.state !== 'solo' || !app.emu?.loaded) return;
  if (app._replayPlaybackActive || app._ghostRaceActive) {
    // The 📂 button stays visible in these modes (they run as SOLO) — give
    // feedback instead of a silent dead click.
    app._flash?.('Saves are unavailable during replays and races');
    return;
  }
  const overlay = document.getElementById('saves-overlay');
  if (!overlay) return;
  _app = app;
  _activeTab = 'manual';
  const title = document.getElementById('saves-overlay-game');
  if (title) title.textContent = app._activeGameTitle?.() || app.romName || '';
  app.acquireGameplayInput('saves-panel');
  overlay.classList.remove('hidden');
  // X-ESC: where the panel opened in the order Esc compares with the game
  // room's top layer (game-keys.js).
  noteSurfaceOpened(overlay);
  document.getElementById('saves-overlay-close')?.focus();
  await refreshSavesPanel(app);
}

export function closeSavesPanel() {
  const overlay = document.getElementById('saves-overlay');
  if (!overlay || overlay.classList.contains('hidden')) return;
  overlay.classList.add('hidden');
  clearTimeout(_deleteTimer);
  _deleteArmedKey = null;
  _app?.releaseGameplayInput?.('saves-panel');
  _app?.canvas?.focus?.();
}

async function gatherRows(app) {
  const hash = primaryHash(app.romHash);
  const serverSaves = app.saveSync?.isLoaded?.()
    ? app.saveSync.getServerSavesForGame(app.system.id, hash)
    : [];
  let localSaves = [];
  try {
    // GB dual-hash local rows store an ARRAY romHash — normalize the row side
    // too, or those rows never merge and the panel shows them cloud-only.
    localSaves = (await listSaves(app.system.id)).filter(s => primaryHash(s.romHash) === hash);
  } catch { /* IndexedDB unavailable — server rows still render */ }
  return buildSaveRows(serverSaves, localSaves);
}

/**
 * Show `Saves N/10` for the current game's manual (state) saves when the
 * `save_slot_cap` flag is on; hidden otherwise. N counts the merged manual
 * rows (server + local, deduped) — the same set the cap bounds. Flag OFF →
 * the count element is cleared so the panel reads as unbounded (current).
 */
function renderSaveCount(rows) {
  const tabsEl = document.getElementById('saves-overlay-tabs');
  let el = document.getElementById('saves-overlay-count');
  // EXPLICIT-positive gate (not isEnabled, which is opt-out): only show the
  // N/10 cap when the flag is genuinely loaded as true. If the feature fetch
  // failed (or the flag is absent), getFeatures().save_slot_cap is undefined
  // and we stay unbounded — the count must not imply a cap the server isn't
  // enforcing.
  if (moduleRegistry.getFeatures().save_slot_cap !== true) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'saves-overlay-count';
    el.className = 'saves-overlay-count';
    // Sits above the tabs/list; insert before the tabs container if present.
    tabsEl?.parentNode?.insertBefore(el, tabsEl);
  }
  const n = rows.filter(r => !r.isAuto).length;
  el.textContent = `Saves ${n}/${MANUAL_STATE_CAP}`;
}

export async function refreshSavesPanel(app) {
  const container = document.getElementById('saves-overlay-list');
  const tabsEl = document.getElementById('saves-overlay-tabs');
  if (!container) return;
  const rows = await gatherRows(app);
  renderSaveCount(rows);
  if (!rows.length) {
    if (tabsEl) tabsEl.innerHTML = '';
    container.innerHTML = '<div class="saves-empty">No saves yet. Save now creates one</div>';
    return;
  }
  const manual = rows.filter(r => !r.isAuto);
  const auto = rows.filter(r => r.isAuto);

  if (tabsEl) tabsEl.innerHTML = '';
  if (tabsEl && manual.length && auto.length) {
    for (const [key, label] of [['manual', 'My Saves'], ['auto', 'Auto Saves']]) {
      const tab = document.createElement('button');
      tab.className = `top-games-tab${key === _activeTab ? ' active' : ''}`;
      tab.textContent = label;
      tab.type = 'button';
      tab.addEventListener('click', () => {
        _activeTab = key;
        tabsEl.querySelectorAll('.top-games-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        renderRows(app, key === 'manual' ? manual : auto, container);
      });
      tabsEl.appendChild(tab);
    }
    renderRows(app, _activeTab === 'manual' ? manual : auto, container);
  } else {
    renderRows(app, manual.length ? manual : auto, container);
  }
}

function renderRows(app, rows, container) {
  // A re-render rebuilds every button, so any pending two-tap arm state
  // refers to DOM that no longer exists — reset it or the freshly-rendered
  // (visually disarmed) button would delete on its first tap.
  clearTimeout(_deleteTimer);
  _deleteArmedKey = null;
  container.innerHTML = '';
  for (const row of rows) {
    const entry = document.createElement('div');
    entry.className = row.isAuto ? 'save-entry auto-save' : 'save-entry';
    entry.dataset.saveKey = row.key;
    const dateStr = row.date
      ? new Date(row.date).toLocaleDateString() + ' '
        + new Date(row.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : '';
    const device = row.server ? (row.deviceLabel || '') : 'This device';
    const metaBits = [dateStr, app._timeAgo?.(row.date), device].filter(Boolean);
    // Static skeleton only — name/meta are user-influenceable (save labels,
    // device labels) and _escapeHtml doesn't cover the attribute context, so
    // they are assigned via textContent/setAttribute below.
    entry.innerHTML =
      `<div class="save-info">` +
        `<div class="save-name"></div>` +
        `<div class="save-meta"></div>` +
      `</div>` +
      `<div class="save-actions">` +
        `<button class="btn btn-sm save-load-btn" type="button">Load</button>` +
        `<button class="save-action-btn delete" type="button" title="Delete">✕</button>` +
      `</div>`;
    const nameEl = entry.querySelector('.save-name');
    nameEl.textContent = row.name;
    nameEl.setAttribute('title', row.name);
    entry.querySelector('.save-meta').textContent = metaBits.join(' · ');
    entry.querySelector('.save-meta').setAttribute('title', metaBits.join(' · '));
    entry.querySelector('.save-load-btn')
      .addEventListener('click', (e) => { loadSaveRow(app, row, e.currentTarget).catch(() => {}); });
    entry.querySelector('.delete')
      .addEventListener('click', (e) => { deleteRow(app, row, e.currentTarget).catch(() => {}); });
    container.appendChild(entry);
  }
}

/**
 * Load a row: in-place apply for same-ROM single-file saves; reboot-path
 * fallback for multi-disk / BASIC / stale-hash saves. Merged rows load the
 * server copy (authoritative — see slot-collision note in the header).
 */
async function loadSaveRow(app, row, btnEl) {
  if (btnEl?.disabled) return;
  if (btnEl) btnEl.disabled = true;
  try {
    const saveTargetHelpers = await import('./save-overwrite.js');
    const storageOwnerId = saveTargetHelpers.currentSaveStorageOwnerId();
    const storageNamespace = currentSaveDBNamespace();
    app._flash('Loading…');
    let save = null;
    let serverSha256 = null;
    try {
      if (row.server) {
        const data = await app.saveSync.download(
          row.server.system, row.server.romHash, row.server.kind, row.server.slot);
        if (data) {
          serverSha256 = data.serverSha256 || null;
          save = decodeEmulatorState(JSON.parse(new TextDecoder().decode(data)));
          save.romHash = save.romHash || row.server.romHash;
        }
      } else {
        save = await getSave(row.localId);
      }
    } catch { /* handled by the !save guard below */ }
    // Replay-derived saves (Atari/Genesis) carry an RPL1 `replayLog` and no
    // usable state blob, so accept them on the log alone.
    if (!save?.emulatorState && !save?.replayLog) {
      app._flash('Failed to load save');
      return;
    }
    if (saveTargetHelpers.currentSaveStorageOwnerId() !== storageOwnerId
        || currentSaveDBNamespace() !== storageNamespace) return;

    // Genesis saves and legacy replay-only Atari saves still need a reboot into
    // their matching mode. A combined Atari save is different: its usable state
    // blob and matching RPL1 prefix are designed for an in-place pump load that
    // reseeds the running recorder.
    const saveSystem = save.system || row.server?.system || app.system?.id;
    const isCombinedAtari = saveSystem === 'atari2600'
      && save.emulatorState && save.emulatorState.byteLength !== 0 && save.replayLog;
    const requiresModeReboot = saveSystem === 'genesis'
      || (saveSystem === 'atari2600' && !isCombinedAtari);
    // Both sides can carry a GB dual-hash ARRAY (app.romHash from the session,
    // save.romHash from an IDB row or a cloud blob) — compare primaries so a
    // same-game save isn't misread as stale and bounced to the reboot loaders.
    const currentHash = primaryHash(app.romHash);
    if (save.isMultiDisk || save.isBasicMode || requiresModeReboot
        || (save.romHash && primaryHash(save.romHash) !== currentHash)) {
      closeSavesPanel();
      if (row.server) return app._loadSaveFromServer(row.server);
      return app._loadSaveFromDB(row.localId);
    }

    const saveTarget = saveTargetHelpers.createPanelSaveTarget(
      app, save, row, storageOwnerId, storageNamespace, serverSha256,
    );
    const ok = await app._applySaveInPlace(save, saveTarget);
    if (ok) {
      closeSavesPanel();
      app._flash(`Loaded: ${save.name || row.name}`);
    }
  } finally {
    if (btnEl) btnEl.disabled = false;
  }
}

/**
 * Save the running game via the standard _saveGame() path (naming, N64
 * async serialize, conflict handling, background cloud upload), then
 * refresh: the merge shows the new local row immediately; the server row
 * replaces it on a later refresh once the upload lands in the index.
 */
async function saveNow(app) {
  const btn = document.getElementById('saves-overlay-save-now');
  if (btn?.disabled) return;
  if (btn) btn.disabled = true;
  try {
    await app._saveGame();
    _activeTab = 'manual';
    await refreshSavesPanel(app);
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** Two-tap delete: first tap arms (auto-disarms after 3s), second deletes. */
async function deleteRow(app, row, btnEl) {
  if (_deleteArmedKey !== row.key) {
    const prev = btnEl.closest('.saves-list')?.querySelector('.save-action-btn.armed');
    if (prev && prev !== btnEl) { prev.textContent = '✕'; prev.classList.remove('armed'); }
    _deleteArmedKey = row.key;
    btnEl.textContent = 'Sure?';
    btnEl.classList.add('armed');
    clearTimeout(_deleteTimer);
    _deleteTimer = setTimeout(() => {
      _deleteArmedKey = null;
      btnEl.textContent = '✕';
      btnEl.classList.remove('armed');
    }, 3000);
    return;
  }
  clearTimeout(_deleteTimer);
  _deleteArmedKey = null;
  try {
    // Both sides or neither: a failed server delete must not orphan-delete
    // the local copy — the refresh would resurrect the row from the server
    // index with the local twin gone.
    let serverOk = true;
    if (row.server) serverOk = (await app._deleteServerSave(row.server)) !== false;
    if (serverOk && row.localId != null) await deleteSave(row.localId);
    if (serverOk && app._saveRef) {
      const { clearDeletedSaveTarget } = await import('./save-overwrite.js');
      clearDeletedSaveTarget(app, row);
    }
  } catch {
    app._flash('Failed to delete save');
  }
  await refreshSavesPanel(app);
}
